import { useEffect, useRef, useState } from 'react';
import type { PermissionRequest, UserProfile } from '@shared/protocol';
import { ShieldIcon } from './icons';

interface Props {
  request: PermissionRequest;
  me?: UserProfile;
  onDecide: (id: string, decision: 'allow' | 'deny') => void;
}

function render(input: unknown): string {
  if (typeof input === 'string') return input;
  try {
    return JSON.stringify(input, null, 2);
  } catch {
    return String(input);
  }
}

/** `mcp__figma__use_figma` -> "Figma · use_figma", for the personal-account case. */
function toolLabel(name: string): string {
  const m = /^mcp__([^_]+)__(.+)$/.exec(name);
  if (!m) return name;
  const service = m[1] === 'atlassian' ? 'Atlassian' : m[1]!.charAt(0).toUpperCase() + m[1]!.slice(1);
  return `${service} · ${m[2]}`;
}

export function PermissionDialog({ request, me, onDecide }: Props) {
  // A write with someone's personal account is theirs to allow. The server
  // enforces it; the button only saves a round trip to be told so.
  const actingAs = request.actingAs;
  const mine = !actingAs || actingAs.email.toLowerCase() === me?.email.toLowerCase();

  const [remaining, setRemaining] = useState(() =>
    Math.max(0, Math.round((request.expiresAt - Date.now()) / 1000)),
  );
  const denyRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const tick = () =>
      setRemaining(Math.max(0, Math.round((request.expiresAt - Date.now()) / 1000)));
    tick();
    const id = window.setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [request.expiresAt]);

  // Focus the safe option, not the destructive one.
  useEffect(() => {
    denyRef.current?.focus();
  }, [request.id]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onDecide(request.id, 'deny');
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [request.id, onDecide]);

  const mm = String(Math.floor(remaining / 60)).padStart(2, '0');
  const ss = String(remaining % 60).padStart(2, '0');

  return (
    <div className="scrim" role="dialog" aria-modal="true" aria-labelledby="perm-title">
      <div className="perm">
        <div className="perm__head">
          <span className="perm__badge">
            <ShieldIcon />
          </span>
          <div>
            <div className="perm__title" id="perm-title">
              Claude needs permission
            </div>
            <div className="perm__sub">
              {!actingAs
                ? 'Anyone in this room can decide'
                : mine
                  ? 'Your own account will be used — only you can allow this'
                  : `Uses ${actingAs.name}'s account — only ${actingAs.name} can allow it; anyone can deny`}
            </div>
          </div>
          <span className="perm__countdown">
            {mm}:{ss}
          </span>
        </div>

        <div className="perm__tool">
          <strong>{actingAs ? toolLabel(request.toolName) : request.toolName}</strong>
          {actingAs && <span className="perm__acting"> as {actingAs.name}</span>}
        </div>

        <div className="perm__input">{render(request.input)}</div>

        <div className="perm__actions">
          <button
            ref={denyRef}
            className="button button--danger"
            onClick={() => onDecide(request.id, 'deny')}
          >
            Deny
          </button>
          <button
            className="button button--primary"
            onClick={() => onDecide(request.id, 'allow')}
            disabled={!mine}
            title={mine ? undefined : `Only ${actingAs?.name} can allow this`}
          >
            Allow once
          </button>
        </div>
      </div>
    </div>
  );
}
